import React, { useState, useEffect } from 'react';
import {
  Button,
  FormatText,
  FormContext,
  FormInput,
  FormResetButton,
  FormSelect,
  FormSubmitButton,
  MasterDetailForm,
  Row,
  Widget,
} from '@digital/ui';
import { Modal } from 'antd';
import * as Yup from 'yup';
import { createSasProposta } from '@app/api/sas/sasProposta';
import '../custom.css';
import { getAllStatusSasPropostaOptions } from '@app/api/status';
import { validarCNPJ } from '@app/helpers/validators';
import { FormikProps } from 'formik';
import { getByCnpj, getByCodigo } from '@app/api/fornecedor';
import { convertToSlug, vtStringToBoolean, vtStringToNumbers } from '@app/helpers/stringHelper';
import { reloadVendorListState, reloadWidgetTimelineState } from '../atoms';
import { useRecoilState } from 'recoil';
import { isObjNotEmpty } from '@app/helpers/objHelper';

interface Props {
  ordemCompraId: string;
}

const ModalIncluirVendor = ({ ordemCompraId }: Props) => {
  const [reloadVendorList, setReloadVendorList] = useRecoilState(reloadVendorListState);
  const [reloadWidgetTimeline, setReloadWidgetTimeline] = useRecoilState(reloadWidgetTimelineState);

  const [timer, setTimer] = useState<number | null | NodeJS.Timeout>(null);
  const [exibirModal, setExibirModal] = useState(false);
  const [initialValues, setInitialValues] = useState({ statusSasPropostaId: '1ca99c7d-e52f-4f68-a3a7-40ee54054e50' });
  const [disabledFields, setDisabledFields] = useState(false);
  const [ehInternacional, setEhInternacional] = useState(false);

  const [validationSchema, setValidationSchema] = useState<any>();

  const onReload = () => {
    setReloadVendorList(!reloadVendorList);
    setReloadWidgetTimeline(!reloadWidgetTimeline);
  };

  useEffect(() => {
    if (ehInternacional) {
      setValidationSchema(
        Yup.object().shape({
          emailContato: Yup.string().required('O campo deve ser informado').email('E-mail com formato inválido.'),
          razaoSocial: Yup.string().required('O campo deve ser informado'),
          statusSasPropostaId: Yup.string().required('O campo deve ser informado'),
          nomeContato: Yup.string().required('O campo deve ser informado'),
          telefoneContato: Yup.string().required('O campo deve ser informado'),
        })
      );
    } else {
      setValidationSchema(
        Yup.object().shape({
          cnpj: Yup.string().required('O campo deve ser informado').test('cnpj', 'CNPJ inválido.', validarCNPJ),
          emailContato: Yup.string().required('O campo deve ser informado').email('E-mail com formato inválido.'),
          razaoSocial: Yup.string().required('O campo deve ser informado'),
          statusSasPropostaId: Yup.string().required('O campo deve ser informado'),
          nomeContato: Yup.string().required('O campo deve ser informado'),
          telefoneContato: Yup.string().required('O campo deve ser informado'),
        })
      );
    }
  }, [ehInternacional]);



  const handleCancel = () => {
    setExibirModal(false);
    setDisabledFields(false);
  };

  const handleSubmit = (values: any) => {
    Modal.confirm({
      title: 'Deseja incluir vendor?',
      content:
        'Confirmar a inclusão.',
      cancelText: 'Não',
      onCancel: () => {
        handleCancel();
      },
      okText: 'Sim',
      onOk: () => {
        createSasProposta({ ...values, enviarEmail: true, ordemCompraId: ordemCompraId }).then(data => {
          if (data.valid) {
            handleCancel();
          }
          onReload();
        });
      },
    });
  };

  const handleChangeCodigoTotvs = (value: string, formProps: FormikProps<any> | undefined) => {
    if (formProps) {
      if (value.length > 0) {
        if (timer) {
          clearTimeout(timer);
          setTimer(null);
        }
      } else {
        return;
      }
      setTimer(
        setTimeout(() => {

          getByCodigo(convertToSlug(value), ordemCompraId)
            .then((data: any) => {
              if (isObjNotEmpty(data) && 'codigoTotvs' in data) {
                setFornecedorFields(formProps, data);
                setDisabledFields(true);
              }
            });

        }, 1000)
      );
    }
  };

  const handleChangeCnpj = (value: string, formProps: FormikProps<any> | undefined) => {
    if (ehInternacional) {
      return;
    }
    if (formProps) {
      if (value.length > 0) {
        if (timer) {
          clearTimeout(timer);
          setTimer(null);
        }
      } else {
        return;
      }
      setTimer(
        setTimeout(() => {

          getByCnpj(convertToSlug(value), ordemCompraId)
            .then((data: any) => {
              if (isObjNotEmpty(data) && 'cnpj' in data) {
                setFornecedorFields(formProps, data);
                setDisabledFields(true);
              }
            });

        }, 1000)
      );
    }
  };


  const setFornecedorFields = (formProps: FormikProps<any>, data: any) => {
    if (isObjNotEmpty(data)) {
      if (isObjNotEmpty(data.cnpj)) {
        formProps.setFieldValue('cnpj', data.cnpj);
        setEhInternacional(false);
      } else {
        setEhInternacional(true);
      }

      formProps.setFieldValue('razaoSocial', data.razaoSocial);
      formProps.setFieldValue('codigoTotvs', data.codigoTotvs);
      formProps.setFieldValue('emailContato', data.emailContato);
      formProps.setFieldValue('nomeContato', data.nomeContato);
      formProps.setFieldValue('telefoneContato', data.telefoneContato);
    }
  };

  return (
    <>
      <Button
        title="incluir"
        className="incluir-vendor"
        onClick={() => {
          setExibirModal(true);
          setEhInternacional(false);
        }}
      />
      {exibirModal && (
        <Modal title="Inclusão Vendor List" width="980px" visible={exibirModal} onCancel={handleCancel} footer={null}>
          <MasterDetailForm
            key="formVendor"
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            actions={[
              <FormResetButton
                type="link"
                noIcon
                beforeReset={() => {
                  setEhInternacional(false);
                  setDisabledFields(false);
                }}>
                Limpar
              </FormResetButton>,
              <Button
                type="link"
                onClick={() => {
                  handleCancel();
                }}
                key="close">
                Fechar
              </Button>,
              <FormSubmitButton noIcon type="primary">
                <FormatText id="ui.commons.save" uppercase />
              </FormSubmitButton>,
            ]}>
            <FormContext.Consumer>
              {({ formProps }) => {
                return (
                  <Widget>
                    <Row>
                      <FormSelect
                        label="Natureza fornecedor"
                        placeholder="Natureza fornecedor"
                        options={[
                          { value: '0', text: 'Nacional' },
                          { value: '1', text: 'Internacional' },
                        ]}
                        name="naturezaFornecedor"
                        span={3}
                        onSelect={(value: any, option) => {
                          formProps && formProps.resetForm();
                          setDisabledFields(false);
                          setEhInternacional(vtStringToBoolean(value));
                        }}
                        value={ehInternacional == true ? "1" : "0"}
                      />
                    </Row>
                    <Row>
                      <FormInput
                        label="Código Totvs"
                        name="codigoTotvs"
                        span={3}
                        onChange={e => handleChangeCodigoTotvs(e.currentTarget.value, formProps)}
                        disabled={disabledFields}
                        autoComplete='off'
                      />
                      <FormInput
                        label="CNPJ"
                        name="cnpj"
                        span={3}
                        value={formProps && vtStringToNumbers(formProps.values.cnpj)}
                        onChange={e => handleChangeCnpj(e.currentTarget.value, formProps)}
                        disabled={ehInternacional || disabledFields}
                        autoComplete='off'
                      />
                      <FormInput
                        label="Razão Social"
                        name="razaoSocial"
                        span={6}
                        disabled={disabledFields}
                        autoComplete='off'
                      />
                    </Row>
                    <Row>
                      <FormInput label="E-mail Fornecedor" name="emailContato" span={8} />
                      <FormSelect
                        label="Status desta Cotação"
                        getOptions={getAllStatusSasPropostaOptions}
                        name="statusSasPropostaId"
                        span={4}
                        disabled
                      />
                    </Row>
                    <Row>
                      <FormInput label="Nome Contato" name="nomeContato" span={8} />
                      <FormInput label="Telefone Contato" name="telefoneContato" span={4} />
                    </Row>
                  </Widget>
                );
              }}
            </FormContext.Consumer>
          </MasterDetailForm>
        </Modal>
      )}
    </>
  );
};
export default ModalIncluirVendor;
